import {container, inject, singleton} from "tsyringe";
import {BackendClient} from "../connectivity/backend-client";
import {OrderParameter} from "../connectivity/order-parameter.interface";
import {QueryParameter} from "../connectivity/query-parameter.interface";
import {DataTransferObject} from "../domain/dtos/data-transfer-object.interface";
import {Entity} from "../domain/entities/entity";
import {IEvent} from "../events/i-event.interface";
import {StringHelper} from "../helpers/string.helper";
import {CommandSendPayload} from "../message-broker/payloads/command-send.payload";
import {ConfigurationManager} from "../services/configuration.manager";
import {EventBus} from "../services/event-bus.service";
import {ClassOf} from "../types/class-of.type";
import {ValueOf} from "../types/value-of.type";
import {EntityMetaData} from "./entity-meta-data.interface";
import {EntityState} from "./entity-state.enum";
import {Repository} from "./repository";
import {UsedColor} from "../types/used-color.type";

@singleton()
export class EntityManager {

    private _entityMetaDates = new Map<string, EntityMetaData>();

    private _repositoryRegister = new Map<ClassOf<Entity>, ClassOf<Repository<any>>>();

    /** Tracks in-flight query requests keyed by a stable string representation of the parameters. */
    private _inflightRequests = new Map<string, Promise<any[]>>();

    constructor(
        @inject(ConfigurationManager) private readonly _configurationManager: ConfigurationManager,
        @inject(BackendClient) private readonly _backendClient: BackendClient,
        @inject(EventBus) private readonly _eventBus: EventBus
    ) {
        this._eventBus.listenForCommandSendByPattern(new RegExp(`^create\.`)).subscribe(this._onEntityCreated.bind(this));
    }

    public findAllByEntityType<T extends Entity>(_entityType: typeof Entity): T[] {
        return Array.from(this._entityMetaDates.values()).map(metaData => metaData.entity).filter(entity => entity.constructor === _entityType) as T[];
    }

    public async findAllByQueryParameters<T extends DataTransferObject>(
        entityName: string,
        queryParams: QueryParameter<any>[] = [],
        includes: string[] = [],
        limit = -1,
        offset = 0,
        options?: { includeDeleted?: boolean }
    ): Promise<T[]> {
        return (await this.query<T>(
            entityName,
            queryParams,
            offset,
            limit,
            [],
            includes,
            options
        )) ?? [];
    }

    public async findOneBy<T extends Entity>(entityName: string, field: keyof T, value: ValueOf<T>): Promise<DataTransferObject | null> {

        const entityDTOs = await this.query<DataTransferObject>(
            entityName,
            [<QueryParameter<DataTransferObject>>{
                key: field,
                comparator: "eq",
                value: value
            }]
        );

        if (!entityDTOs) {
            return null;
        }

        // Read, never `shift()` — it used to empty the in-flight result every concurrent
        // caller shared, so a second parallel `appState.employee` lookup resolved `null` and
        // the My Organizer inbox loaded "nothing assigned to me" (bug, Anke 2026-10-16).
        // `query()` now hands each caller its own array as well (see there).
        return entityDTOs[0] ?? null;
    }

    public getLoadedEntity(uid: string): Entity | null {
        return this._entityMetaDates.get(uid)?.entity ?? null;
    }

    public getRepositoryForEntity<T extends Entity>(entity: ClassOf<Entity>): Repository<T> | null {
        if (!this._repositoryRegister.has(entity)) {
            return null;
        }
        const repositoryClass = this._repositoryRegister.get(entity)!;

        return container.resolve(repositoryClass);
    }

    public getRepositoryForEntityByName<TEntity extends Entity>(name: string): Repository<TEntity> | null {
        const key = Array.from(this._repositoryRegister.keys()).find((entity) => StringHelper.toKebabCase(entity.name) === name);
        if (!key) {
            return null;
        }

        return this.getRepositoryForEntity<TEntity>(key);
    }

    public isLoaded(uid: string): boolean {
        return this._entityMetaDates.has(uid) && this._entityMetaDates.get(uid)!.state !== EntityState.LOADING;
    }

    public isNew(uid: string): boolean {
        if (!this._entityMetaDates.has(uid)) {
            return true;
        }

        return this._entityMetaDates.get(uid)!.state === EntityState.NEW;
    }

    public isVolatile(uid: string): boolean {
        return !(this._entityMetaDates.has(uid) && this._entityMetaDates.get(uid)!.state !== EntityState.NEW);
    }

    public markAsPersisted(entity: Entity): void {
        // Only skip if entity is already persisted
        if (this._entityMetaDates.has(entity.uid) && this._entityMetaDates.get(entity.uid)!.state === EntityState.PERSISTED) {
            return;
        }

        // Update entity state in manager FIRST (before triggering observable)
        this.setLoadedEntity(entity, EntityState.PERSISTED);
        
        // THEN mark entity as persisted (triggers persisted$ observable)
        entity.markAsPersisted();
    }

    public registerRepositoryForEntity(entity: ClassOf<Entity>, repository: ClassOf<Repository<any>>): void {
        this._repositoryRegister.set(entity, repository);
    }

    public setLoadedEntity(entity: Entity, state: EntityState): void {
        // Guard: do not overwrite an already-persisted entity (race condition between entity.created and preload)
        if (state === EntityState.PERSISTED && this._entityMetaDates.has(entity.uid) && this._entityMetaDates.get(entity.uid)!.state === EntityState.PERSISTED) {
            return;
        }

        this._entityMetaDates.set(entity.uid, {
            state,
            entity
        });
    }

    public unloadEntity<T extends Entity>(entity: T): void {
        if (this._entityMetaDates.has(entity.uid)) {
            this._entityMetaDates.delete(entity.uid);
        }

        const repository = this.getRepositoryForEntity(entity.constructor as ClassOf<T>);

        repository!.unCachingEntity(entity);
    }

    /**
     * Loads the colours already used by entities of the given type. The backend always
     * answers with a list; the client resolves null only for an HTTP 204, which this
     * endpoint does not send, so null is mapped to the empty list.
     */
    public async findAllColors(entityName: string): Promise<UsedColor[]> {
        const host = this._configurationManager.get<string>(`backend.host`);
        const uri = `${host}/api/v1/colors/${StringHelper.toKebabCase(entityName)}`;

        return (await this._backendClient.get<UsedColor[]>(uri)) ?? [];
    }

    public async findAllBy<T extends Entity>(entityName: string, field: keyof T, values: Array<ValueOf<T>>): Promise<DataTransferObject[]> {

        const entityDTOs = await this.query<DataTransferObject>(
            entityName,
            [<QueryParameter<DataTransferObject>>{
                key: field,
                comparator: "in",
                value: values
            }]
        );

        return entityDTOs ?? [];
    }

    /**
     * Runs a backend query; concurrent calls with identical parameters share ONE request.
     *
     * Every caller still gets its OWN shallow copy of the result array, so sorting or
     * removing entries in one caller can never empty the result another caller is still
     * awaiting (bug 2026-10-16, `findOneBy` used to `shift()` the shared array). The DTO
     * objects inside are shared — read them, do not mutate them.
     */
    public query<T>(
        entityName: string,
        queryParams: QueryParameter<T>[],
        offset = 0,
        limit: number = 25,
        orderBy: OrderParameter<T>[] = [],
        includes: string[] = [],
        options?: { includeDeleted?: boolean }
    ): Promise<T[] | null> {
        const includeDeleted = options?.includeDeleted ?? false;
        const key = this._buildQueryKey(entityName, queryParams, offset, limit, orderBy, includes, includeDeleted);

        if (this._inflightRequests.has(key)) {
            return EntityManager._ownCopy(this._inflightRequests.get(key)! as Promise<T[] | null>);
        }

        const host = this._configurationManager.get<string>(`backend.host`);
        const uri = `${host}/api/v1/query/${StringHelper.toKebabCase(entityName)}`;
        const requestBody = Object.assign({
            queryParams,
            offset,
            orderBy,
            includes,
            includeDeleted
        }, limit >= 0 ? {limit} : {});

        const request = this._backendClient.post(uri, false, requestBody) as Promise<any[]>;
        this._inflightRequests.set(key, request);
        // Cleanup the map entry on settle. .catch() silences the rejection on the cleanup chain
        // to avoid an unhandled-rejection warning — the callers' copies (`_ownCopy`) still reject.
        request.finally(() => this._inflightRequests.delete(key)).catch(() => {});

        return EntityManager._ownCopy(request as Promise<T[] | null>);
    }

    /** A caller's own copy of a shared in-flight result (see `query()`); a rejection passes through. */
    private static _ownCopy<T>(shared: Promise<T[] | null>): Promise<T[] | null> {
        return shared.then(rows => (Array.isArray(rows) ? [...rows] : rows));
    }

    /**
     * Builds a stable, unique cache key from query parameters.
     * Uses sorted property keys in JSON.stringify to ensure stability
     * regardless of object construction order.
     */
    private _buildQueryKey(
        entityName: string,
        queryParams: QueryParameter<any>[],
        offset: number,
        limit: number,
        orderBy: OrderParameter<any>[],
        includes: string[],
        includeDeleted: boolean
    ): string {
        const stableStringify = (obj: unknown): string =>
            JSON.stringify(obj, obj && typeof obj === 'object' && !Array.isArray(obj) ? Object.keys(obj as object).sort() : undefined);

        return `${entityName}:${stableStringify(queryParams)}:${offset}:${limit}:${stableStringify(orderBy)}:${stableStringify(includes)}:${includeDeleted}`;
    }

    private _onEntityCreated(event: IEvent<CommandSendPayload>): void {
        const {success, command} = event.payload!;
        this.setLoadedEntity(command.payload, EntityState.PERSISTED);
        // Feature plan 0074: a record created on its own (not in a bulk) never reaches `entity.markAsPersisted()`
        // — this listener already marks it PERSISTED, so `EntityManager.markAsPersisted` returns early. Give its
        // colours a starting value here, or a later change of the text colour in this session is lost. Only on
        // success, and only for a real entity: `create.linkview`/`create.link-tile` carry a plain object.
        // `setLoadedEntity` above stays as it was, also on failure (separate follow-up of ADR-016).
        if (success && command.payload instanceof Entity) {
            command.payload.establishColorBaseline();
        }
    }
}